import { useCallback, useMemo, useState } from 'react';
import type {
  ColumnDef,
  DefaultSortState,
  FilterState,
  FilterValue,
  SortState,
  UseTableFiltersReturn,
} from '@/shared/types/table-filters.types';
import {
  applyTableQuery,
  countActiveFilters,
  nextSort,
  DEFAULT_TABLE_SORT,
} from '@/shared/utils/tableFilters';

/**
 * Client-side search, filtering and sorting for small, fully-loaded lists.
 * The semantics live in `@/shared/utils/tableFilters`; this hook only owns the state.
 */
export function useTableFilters<T>(
  data: T[],
  columns: ColumnDef<T>[],
  defaultSort: DefaultSortState = DEFAULT_TABLE_SORT
): UseTableFiltersReturn<T> {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<FilterState>({});
  const [sort, setSort] = useState<SortState | null>(null);

  const setFilter = useCallback((key: string, value: FilterValue | undefined): void => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  }, []);

  const clearFilters = useCallback((): void => setFilters({}), []);

  const toggleSort = useCallback((key: string): void => {
    setSort((previous) => nextSort(previous, key));
  }, []);

  const filteredSorted = useMemo(
    () => applyTableQuery(data, columns, { searchTerm, filters, sort, defaultSort }),
    [data, columns, searchTerm, filters, sort, defaultSort]
  );

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  return {
    filteredSorted,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    sort,
    setSort,
    toggleSort,
    activeFilterCount,
  };
}
